import React, { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Search, User as UserIcon } from "lucide-react";
import { Input } from "../ui/input";
import { superAdminApi, type SearchResults } from "../../lib/superadmin-api";
import { roleLabel } from "../../lib/role-labels";

import { useTranslation } from "react-i18next";
interface Hit {
  key: string;
  to: string;
  icon: React.ReactNode;
  title: string;
  detail?: string;
}

const MIN_LENGTH = 2;

function toHits(results: SearchResults, disabledLabel: string): Hit[] {
  return [
    ...results.clubs.map((c) => ({
      key: `club-${c.id}`,
      to: `/super-admin/clubs/${c.id}`,
      icon: <Building2 className="h-4 w-4 shrink-0" aria-hidden />,
      title: c.name,
      detail: "Club",
    })),
    ...results.people.map((p) => ({
      key: `person-${p.id}`,
      to: `/super-admin/people/${p.id}`,
      icon: <UserIcon className="h-4 w-4 shrink-0" aria-hidden />,
      title: p.name,
      detail: [p.isDisabled ? disabledLabel : null, p.email, p.isSuperAdmin ? "Super admin" : p.clubs.map((c) => `${c.name} (${roleLabel(c.role)})`).join(", ")].filter(Boolean).join(" · "),
    })),
  ];
}

/** One box for "the person on the phone": a club name, a person's name or an email. */
export function GlobalSearch() {
  const { t } = useTranslation("superadmin");
  const navigate = useNavigate();
  const listId = useId();
  const [text, setText] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "done">("idle");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const latest = useRef(0);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const query = text.trim();
    if (query.length < MIN_LENGTH) {
      setHits([]);
      setStatus("idle");
      return;
    }
    const ticket = ++latest.current;
    setStatus("loading");
    const timer = window.setTimeout(() => {
      superAdminApi
        .search(query)
        .then((results) => {
          if (ticket !== latest.current) return; // a newer search is on its way
          setHits(toHits(results, t("globalSearch.disabled")));
          setActive(0);
          setStatus("done");
        })
        .catch(() => ticket === latest.current && setStatus("error"));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (hit: Hit) => {
    setOpen(false);
    setText("");
    navigate(hit.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") return setOpen(false);
    if (!hits.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + hits.length) % hits.length);
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      go(hits[active]);
    }
  };

  const showPanel = open && text.trim().length >= MIN_LENGTH;

  return (
    <div ref={root} className="relative w-full">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        type="search"
        role="combobox"
        aria-label={t("globalSearch.rechercherUnClubOuUne")}
        aria-expanded={showPanel}
        aria-controls={listId}
        aria-activedescendant={showPanel && hits[active] ? `${listId}-${hits[active].key}` : undefined}
        autoComplete="off"
        placeholder={t("globalSearch.rechercherUnClubUnNom")}
        className="pl-9"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showPanel && (
        <div className="absolute right-0 top-full z-40 mt-1 max-h-96 w-full overflow-y-auto rounded-md border bg-card shadow-lg md:w-[32rem] md:max-w-[calc(100vw-2rem)]">
          {status === "loading" && hits.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">{t("globalSearch.recherche")}</p>}
          {status === "error" && <p className="px-3 py-3 text-sm text-destructive">{t("globalSearch.laRechercheAEchoue")}</p>}
          {status === "done" && hits.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">Aucun résultat pour « {text.trim()} ».</p>}
          <ul id={listId} role="listbox" aria-label={t("globalSearch.resultats")}>
            {hits.map((hit, i) => (
              <li
                key={hit.key}
                id={`${listId}-${hit.key}`}
                role="option"
                aria-selected={i === active}
                className={`flex cursor-pointer items-start gap-3 px-3 py-2 text-sm ${i === active ? "bg-muted" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => go(hit)}
              >
                <span className="mt-0.5 text-muted-foreground">{hit.icon}</span>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{hit.title}</span>
                  {hit.detail && <span className="block break-words text-xs text-muted-foreground">{hit.detail}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
